/** @format */

import { isPlatformBrowser } from '@angular/common';

import {
	ChangeDetectorRef,
	Component,
	inject,
	Input,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	Signal,
	viewChild,
	ViewChild,
	ViewContainerRef
} from '@angular/core';

import { chain } from '@core/chain';
import { DeviceService, UtilityService } from '@shared/utils/services';
import { Store as NgRxStore } from '@ngrx/store';
import {
	selectKillswitch,
	selectPrimaryCategory,
	setBopisInventory,
	setEcommInventory,
	setPromotionData
} from '@pdp/data-access/state';
import { BreakPointNew } from '@shared/data-access/enums';
import { take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import {
	Breadcrumb,
	KillSwitches,
	ProcessedNonBundleProduct,
	ProductListSEO,
	ProductSku
} from '@shared/data-access/interfaces';
import { DynamicProductTemplateModules } from '@pdp/data-access/enums';
import { ReviewHighlyRatedFeaturesModalComponent } from '@product-reviews';

@Component({
	selector: 'pdp-templates-out-of-stock-default',
	templateUrl: './out-of-stock-default.component.html',
	styleUrls: ['./out-of-stock-default.component.scss'],
	standalone: false
})
export class OutOfStockDefaultComponent implements OnInit, OnDestroy {
	private readonly utilityService = inject(UtilityService);
	protected readonly platformId = inject(PLATFORM_ID);
	private readonly deviceService = inject(DeviceService);
	private readonly ngRxstore = inject(NgRxStore);
	private readonly changeDetectorRef = inject(ChangeDetectorRef);

	/**
	 * @description
	 * Reference to the view position where the above the fold content will be placed
	 */
	@ViewChild('targetAboveTheFoldViewRef', { read: ViewContainerRef })
	targetAboveTheFoldViewRef: ViewContainerRef;

	highlyRatedFeaturesModal: Signal<ReviewHighlyRatedFeaturesModalComponent> = viewChild.required(
		'highlyRatedFeaturesModal'
	);

	/**
	 * @description
	 * Reference to the initial view position of the section before it is moved into this
	 * component's template
	 */
	@Input() sourceAboveTheFoldViewRef: ViewContainerRef;

	/**
	 * @description
	 * The product currently being displayed
	 */
	@Input() product: ProcessedNonBundleProduct;

	/**
	 * @description
	 * The kill switches
	 */
	@Input() killSwitches: KillSwitches;

	resolvedSku: ProductSku;

	/**
	 * @description
	 * The current chain
	 */
	chain = chain;

	/**
	 * @description
	 * The currently selected color attribute value if applicable
	 */
	selectedColorAttribute: string;

	/**
	 * @description
	 * killswitch for sustainability icons
	 */
	sustainablityIconsEnabled: boolean;

	/**
	 * @description
	 * Determines whether to show the info summary on product description
	 */
	infoSummaryEnabled: boolean;

	/**
	 * @description
	 * feature flag for breadcrumbs
	 */
	browseBreadcrumbsEnabled: boolean;

	primaryCategoryBreadcrumb: string;

	oosBreadCrumbData: Breadcrumb;

	/**
	 * @description
	 * If it's mobile viewport
	 * NOTE: Please don't initialize this flag. The undefined value is used for loading
	 * mobile components in SSR and then hidden them in the browser using media queries
	 * when the viewport is for desktop
	 */
	isMobile: boolean;

	/**
	 * @description
	 * Feature flag review widget.
	 */
	reviewsOff = false;

	/**
	 * @description
	 * Feature flag new reviews widget.
	 */
	newReviews = false;
	reviewsMVPRetest = false;
	newReviewsEnhancement = false;

	accordionContentEnabled = false;

	isLoadingStateRecsEnabled: boolean;
	isReviewImgPlacementEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('isReviewImgPlacementEnabled')
	);

	oosImageUrl = '';

	private subscriptions = new Subscription();

	/**
	 * @description
	 * replaces the isPlatformBrowser(this.platformId) function call
	 */
	isPlatformBrowser = false;
	displayableDescriptiveAttributes = [];
	useAboveTheFoldMobileContainer = false;
	wasAboveTheFoldContentLoaded = false;

	/**
	 * @description
	 * Feature flag to show the top 3 highly rated features
	 */
	isHighlyRatedReviewsEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('isHighlyRatedReviewsEnabled')
	);

	readonly templateName = DynamicProductTemplateModules.OUT_OF_STOCK_DEFAULT as const;

	ngOnInit() {
		this.isPlatformBrowser = isPlatformBrowser(this.platformId);
		this.newReviews = this.utilityService.checkKillSwitch(
			'newReviews',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);
		this.reviewsMVPRetest = this.utilityService.checkKillSwitch(
			'reviewsMVPRetest',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);
		this.newReviewsEnhancement = this.utilityService.checkKillSwitch(
			'newReviewsEnhancement',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);
		this.browseBreadcrumbsEnabled = this.utilityService.checkKillSwitch(
			'browseBreadcrumbsEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);
		this.setBreadCrumbData();
		this.useAboveTheFoldMobileContainer = this.utilityService.checkKillSwitch(
			'useAboveTheFoldMobileContainer',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.isLoadingStateRecsEnabled = this.utilityService.checkKillSwitch(
			'isLoadingStateRecsEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.oosImageUrl = `https://dks.scene7.com/is/image/GolfGalaxy/${this.product.partNumber}?wid=382&hei=382&fmt=pjpeg`;

		if (this.isPlatformBrowser) {
			const breakpoint = this.deviceService.getDeviceBreakPointNew();
			this.isMobile = breakpoint <= BreakPointNew.SMALL;

			// Ensure there is something that was set
			if (typeof this.product !== 'undefined' && this.product !== null) {
				// Default the selected color attribute
				this.selectedColorAttribute = this.product.defaultColor;
			}
			this.sustainablityIconsEnabled = this.utilityService.checkKillSwitch(
				'sustainablityIconsEnabled',
				this.killSwitches,
				this.chain.chainIdentifierAbbr
			);
			this.infoSummaryEnabled = this.utilityService.checkKillSwitch(
				'infoSummaryEnabled',
				this.killSwitches,
				this.chain.chainIdentifierAbbr
			);

			this.reviewsOff = this.utilityService.checkKillSwitch(
				'reviewsOff',
				this.killSwitches,
				this.chain.chainIdentifierAbbr
			);

			this.accordionContentEnabled = this.utilityService.checkKillSwitch(
				'accordionContentEnabled',
				this.killSwitches,
				this.chain.chainIdentifierAbbr
			);

			this.updateDisplayableAttributes();
		}

		// adding dispatch for analytics to fire
		this.ngRxstore.dispatch(setEcommInventory({ ecommInventory: [] }));

		// adding dispatch for analytics to fire
		this.ngRxstore.dispatch(setBopisInventory({ bopisInventory: [] }));

		// adding dispatch for analytics to fire
		this.ngRxstore.dispatch(setPromotionData({ promotionData: {} }));
	}

	ngAfterViewInit(): void {
		// When the app is loaded in the browser we want to move the Above the Fold content that is initially rendered
		// in the product container by detaching it and then inserting it into this component's template without destroying the instance.
		if (
			isPlatformBrowser(this.platformId) &&
			this.useAboveTheFoldMobileContainer &&
			this.sourceAboveTheFoldViewRef
		) {
			const view = this.sourceAboveTheFoldViewRef.detach();
			// We use this flag to determine if the Above the Fold content was completely moved and remove
			// the temporary styles used for adding a placeholder of the end position of the Above the Fold content
			this.wasAboveTheFoldContentLoaded = true;
			this.targetAboveTheFoldViewRef.insert(view);
			// Fire change detection for wasAboveTheFoldContentLoaded flag
			this.changeDetectorRef.detectChanges();
		}
	}

	imageError() {
		this.oosImageUrl =
			'https://images.dickssportinggoods.com/assets/icons/dsg/ImageUnavailablePDPSwatch.svg';
	}

	setBreadCrumbData() {
		this.subscriptions.add(
			this.ngRxstore
				.select(selectPrimaryCategory)
				.pipe(take(5))
				.subscribe((primaryCategory: ProductListSEO) => {
					this.primaryCategoryBreadcrumb = primaryCategory?.identifier;

					if (
						primaryCategory?.breadCrumbDetails &&
						Array.isArray(primaryCategory?.breadCrumbDetails)
					) {
						this.oosBreadCrumbData =
							primaryCategory.breadCrumbDetails[
								primaryCategory.breadCrumbDetails.length - 1
							];
					}
				})
		);
	}

	get oosCtaText(): string {
		return `SHOP ALL ${this.oosBreadCrumbData?.name}`;
	}

	public setDisplayableAttributes() {
		const { descriptiveAttributes } = this.product;

		if (!Array.isArray(descriptiveAttributes) || descriptiveAttributes.length === 0) {
			return [];
		}

		return descriptiveAttributes.filter((attr) => {
			const isDisplayable = this.utilityService.isTruthy(attr?.isDisplayable);
			const isProTips = attr.name.toLowerCase() === 'dsg pro tips';
			const shouldIncludeProTips = isProTips && this.chain.proTipsFlag;
			const shouldIncludeNonProTips = !isProTips;

			return isDisplayable && (shouldIncludeProTips || shouldIncludeNonProTips);
		});
	}

	public updateDisplayableAttributes(): void {
		this.displayableDescriptiveAttributes = this.setDisplayableAttributes();
	}

	openHighlyRatedFeatures() {
		this.highlyRatedFeaturesModal()?.openHighlyRatedFeatures();
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}
}
